import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useUIStore } from '@/store/uiStore';
import { authApi, posApi } from '@/services/api';
import { ROLES, PosSettings, isCompanyAdmin, isManager } from '@/types';
import {
  LayoutDashboard, ShoppingCart, Package, Users, Truck, Building2, BarChart3, FileText,
  Settings, Menu, Headphones, ChevronDown, UserCog, Barcode, Tags,
  PanelLeftOpen, PanelLeftClose, PanelRightOpen, PanelRightClose, Lock, UserRound, RefreshCw, Loader2, Percent, Tag, Wrench, Clock, Receipt, ShoppingBag, Download, Megaphone,
} from 'lucide-react';
import PakRupeeIcon from '@/components/common/PakRupeeIcon';
import { isDesktopApp } from '@/desktop/electronApi';
import { useDesktopSessionStore } from '@/desktop/desktopSessionStore';
import UserAccountMenu from '@/components/common/UserAccountMenu';
import { resolveFileUrl } from '@/utils/fileUrl';
import { getThemeFromDomain } from '@/utils/themeDomain';
import SwitchCashierModal from '@/components/desktop/SwitchCashierModal';
import MyShiftsModal from '@/components/pos/MyShiftsModal';
import { useSyncStore } from '@/store/syncStore';
import { isLocalFirstMode, syncNow } from '@/offline';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

/**
 * Compact icon-rail sidebar (Option 1).
 * Revert options:
 *   1. Set USE_COMPACT_ICON_RAIL to false below
 *   2. Restore backup: cp src/components/common/Sidebar.backup-before-icon-rail.tsx src/components/common/Sidebar.tsx
 */
const USE_COMPACT_ICON_RAIL = true;

type MenuLeaf = { path: string; label: string; labelKey?: string; allowedRoles?: string[] };
type MenuBranch = {
  label: string;
  labelKey?: string;
  pathPrefix: string;
  children: MenuLeaf[];
  allowedRoles?: string[];
};
type MenuChild = MenuLeaf | MenuBranch;

type MenuItem = {
  path: string;
  label: string;
  labelKey?: string;
  icon: typeof LayoutDashboard;
  allowedRoles?: string[];
  children?: MenuChild[];
  requiresWhatsApp?: boolean;
  requiresShopify?: boolean;
};

function isBranch(child: MenuChild): child is MenuBranch {
  return 'children' in child && Array.isArray((child as MenuBranch).children);
}

const allMenuItems: MenuItem[] = [
  { path: '/dashboard', label: 'Dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER] },
  { path: '/', label: 'POS', labelKey: 'nav.pos', icon: ShoppingCart, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.CASHIER] },
  { path: '/sales', label: 'Sales', labelKey: 'nav.sales', icon: Receipt, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.CASHIER] },
  { path: '/deliveries', label: 'Deliveries', labelKey: 'nav.deliveries', icon: Truck, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.CASHIER, ROLES.DELIVERY_PERSON] },
  { path: '/my-services', label: 'My Services', labelKey: 'nav.myServices', icon: Wrench, allowedRoles: [ROLES.TECHNICIAN] },
  { path: '/services/jobs', label: 'Service Jobs', labelKey: 'nav.serviceJobs', icon: Wrench, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER] },
  { path: '/price-checker', label: 'Price Checker', labelKey: 'nav.priceChecker', icon: Barcode, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.CASHIER] },
  { path: '/price-update', label: 'Price Update', labelKey: 'nav.priceUpdate', icon: Tag, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.CASHIER] },
  { path: '/items', label: 'Products', labelKey: 'nav.products', icon: Package, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER] },
  { path: '/categories', label: 'Categories', labelKey: 'nav.categories', icon: Tags, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER] },
  { path: '/customers', label: 'Customers', labelKey: 'nav.customers', icon: Users, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.CASHIER] },
  { path: '/suppliers', label: 'Suppliers', labelKey: 'nav.suppliers', icon: Building2, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER] },
  { path: '/expenses', label: 'Expenses', labelKey: 'nav.expenses', icon: PakRupeeIcon, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER] },
  { path: '/users', label: 'Users', labelKey: 'nav.users', icon: UserCog, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER] },
  { path: '/commission', label: 'Commission', labelKey: 'nav.commission', icon: Percent, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER] },
  { path: '/promotions', label: 'Promotions', labelKey: 'nav.promotions', icon: Tag, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER] },
  { path: '/marketing', label: 'Marketing', labelKey: 'nav.marketing', icon: Megaphone, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER] },
  { path: '/activity-log', label: 'Activity Log', labelKey: 'nav.activityLog', icon: FileText, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER] },
  { path: '/channel-orders', label: 'Marketplace Orders', labelKey: 'nav.marketplaceOrders', icon: ShoppingBag, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER] },
  { path: '/channel-pricing', label: 'Channel Pricing', labelKey: 'nav.channelPricing', icon: Tag, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER] },
  { path: '/shopify-import', label: 'Shopify Import', labelKey: 'nav.shopifyImport', icon: Download, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER], requiresShopify: true },
  { path: '/analytics', label: 'Analytics', labelKey: 'nav.analytics', icon: BarChart3, allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER] },
  {
    path: '/reports',
    label: 'Reports', labelKey: 'nav.reports',
    icon: FileText,
    allowedRoles: [ROLES.COMPANY_ADMIN, ROLES.MANAGER]
  },
  { path: '/raw-data', label: 'Raw Data Export', labelKey: 'nav.rawDataExport', icon: FileText, allowedRoles: [ROLES.COMPANY_ADMIN] },
  {
    path: '/support',
    label: 'Support',
    labelKey: 'nav.support',
    icon: Headphones,
    allowedRoles: [
      ROLES.COMPANY_ADMIN,
      ROLES.MANAGER,
      ROLES.CASHIER,
      ROLES.TECHNICIAN,
      ROLES.ACCOUNTANT,
      ROLES.DELIVERY_PERSON,
    ],
  },
  { path: '/settings', label: 'Settings', labelKey: 'nav.settings', icon: Settings, allowedRoles: [ROLES.COMPANY_ADMIN] },
];

function isPathActive(pathname: string, path: string) {
  if (path === '/') return pathname === '/';
  return pathname === path || pathname.startsWith(`${path}/`);
}

function childIsActive(pathname: string, child: MenuChild) {
  if (isBranch(child)) {
    return (
      child.children.some((leaf) => isPathActive(pathname, leaf.path))
      || isPathActive(pathname, child.pathPrefix)
    );
  }
  return isPathActive(pathname, child.path);
}

function itemIsActive(pathname: string, item: MenuItem) {
  if (item.children?.length) {
    return isPathActive(pathname, item.path)
      || item.children.some((child) => childIsActive(pathname, child));
  }
  return isPathActive(pathname, item.path);
}

function leafLinkClass(pathname: string, path: string, compact = false) {
  const active = pathname === path || (path === '/' && pathname === '/');
  if (compact) {
    return active
      ? 'bg-primary-500/10 text-primary-400 border-l-2 border-primary-500 -ml-px pl-[calc(0.625rem-2px)] font-medium'
      : 'text-slate-400 hover:bg-white/5 hover:text-slate-200';
  }
  return active
    ? 'bg-primary-600 text-white font-medium'
    : 'text-gray-400 hover:bg-slate-800 hover:text-white';
}

function railIconClass(active: boolean) {
  return active
    ? 'bg-primary-500/15 text-primary-400 ring-1 ring-inset ring-primary-500/25'
    : 'text-slate-400 hover:bg-white/5 hover:text-slate-200';
}

type FlyoutPanelProps = {
  item: MenuItem;
  pathname: string;
  openBranches: Record<string, boolean>;
  onToggleBranch: (key: string, isActive: boolean) => void;
  onNavigate: () => void;
};

function FlyoutPanel({ item, pathname, openBranches, onToggleBranch, onNavigate }: FlyoutPanelProps) {
  const { t } = useTranslation();
  const labelOf = (entry: { label: string; labelKey?: string }) =>
    entry.labelKey ? t(entry.labelKey) : entry.label;

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-slate-800 shrink-0">
        <p className="text-sm font-semibold text-white">{labelOf(item)}</p>
      </div>
      <div className="flex-1 overflow-y-auto overscroll-contain p-2 space-y-1">
        {item.children!.map((child) => {
          if (!isBranch(child)) {
            return (
              <Link
                key={child.path}
                to={child.path}
                onClick={onNavigate}
                className={`block px-2.5 py-1.5 text-[13px] rounded-md transition-colors duration-150 ${leafLinkClass(pathname, child.path, true)}`}
              >
                {labelOf(child)}
              </Link>
            );
          }

          const branchKey = child.pathPrefix;
          const branchActive = childIsActive(pathname, child);
          const branchOpen = openBranches[branchKey] ?? branchActive;

          return (
            <div key={branchKey} className="space-y-0.5">
              <button
                type="button"
                onClick={() => onToggleBranch(branchKey, branchActive)}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-[13px] rounded-md transition-colors duration-150 ${branchActive || branchOpen
                  ? 'bg-slate-800/80 text-white'
                  : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
                  }`}
              >
                <span className="flex-1 text-left truncate">{labelOf(child)}</span>
                <ChevronDown
                  className={`w-3.5 h-3.5 shrink-0 opacity-70 transition-transform ${branchOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {branchOpen && (
                <div className="ml-2 pl-2 border-l border-slate-700/80 space-y-0.5">
                  {child.children.map((leaf) => (
                    <Link
                      key={leaf.path}
                      to={leaf.path}
                      onClick={onNavigate}
                      className={`block px-2.5 py-1.5 text-[13px] rounded-md transition-colors duration-150 ${leafLinkClass(pathname, leaf.path, true)}`}
                    >
                      {labelOf(leaf)}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Sidebar() {
  const { t, i18n } = useTranslation();
  const theme = getThemeFromDomain();
  const location = useLocation();
  const isRtl = document.documentElement.dir === 'rtl';
  const navigate = useNavigate();
  const { role, clearAuth, permissions } = useAuthStore();
  const { isSidebarOpen, toggleSidebar } = useUIStore();
  const labelOf = (item: { label: string; labelKey?: string }) =>
    item.labelKey ? t(item.labelKey) : item.label;
  const lockScreen = useDesktopSessionStore((s) => s.lockScreen);
  const clearLocalUnlock = useDesktopSessionStore((s) => s.clearLocalUnlock);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [myShiftsOpen, setMyShiftsOpen] = useState(false);
  const qc = useQueryClient();
  const pendingQueue = useSyncStore((s) => s.pendingQueueCount);
  const failedQueue = useSyncStore((s) => s.failedQueueCount);
  const isSyncing = useSyncStore((s) => s.isSyncing);
  const posSyncMode = useSyncStore((s) => s.posSyncMode);
  const showSync =
    isLocalFirstMode(posSyncMode) && (isCompanyAdmin(role) || isManager(role));
  const [syncBusy, setSyncBusy] = useState(false);
  const { data: posSettings } = useQuery({
    queryKey: ['posSettings'],
    queryFn: async () => {
      const res = await posApi.getPosSettings();
      return res.data.message as PosSettings;
    },
  });
  const { data: companyProfile } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const res = await authApi.getCompanyProfile();
      return res.data.message as {
        enable_whatsapp_shop?: boolean | number;
        enable_shopify?: boolean | number;
        allow_shopify?: boolean | number;
        allow_whatsapp?: boolean | number;
        company_logo?: string | null;
        company_name?: string | null;
        package?: { shopify_module?: boolean };
      };
    },
    staleTime: 60_000,
  });
  const whatsappEnabled =
    (companyProfile?.enable_whatsapp_shop === true || companyProfile?.enable_whatsapp_shop === 1)
    && Boolean(companyProfile?.allow_whatsapp);
  const shopifyEnabled =
    (companyProfile?.enable_shopify === true || companyProfile?.enable_shopify === 1)
    && Boolean(companyProfile?.allow_shopify);
  const shopLogoUrl = companyProfile?.company_logo
    ? resolveFileUrl(companyProfile.company_logo)
    : '';
  const platformLogoSrc =
    theme === 'kashly'
      ? i18n.language === 'ar'
        ? '/kashly-logo-ar.png'
        : '/kashly-logo-en.png'
      : '/saledesk-logo.svg';
  const platformLogoAlt = theme === 'kashly' ? 'Kashly' : 'Sale Desk';
  const headerLogoSrc = shopLogoUrl || platformLogoSrc;
  const headerLogoAlt = shopLogoUrl
    ? companyProfile?.company_name || platformLogoAlt
    : platformLogoAlt;
  const platformLogoClass =
    'h-7 w-auto max-w-[9rem] object-contain bg-white rounded px-1.5 py-0.5';
  const headerLogoClass = shopLogoUrl
    ? 'h-[52px] w-auto max-w-[9rem] object-contain bg-white rounded p-1'
    : platformLogoClass.replace('h-7', 'h-9');

  const menuItems = useMemo(
    () => {
      let items = allMenuItems.filter((item) => {
        if (item.requiresWhatsApp && !whatsappEnabled) return false;
        if (item.requiresShopify && !shopifyEnabled) return false;
        if (item.path === '/support' && theme === 'kashly') return false;
        if (item.path === '/price-update') {
          if (isCompanyAdmin(role) || isManager(role)) return true;
          return role === ROLES.CASHIER && !!permissions?.can_update_item_prices;
        }
        if (!item.allowedRoles) return true;
        return item.allowedRoles.includes(role);
      }).map((item) => {
        if (!item.children) return item;
        return {
          ...item,
          children: item.children.filter((child) => {
            if (!('allowedRoles' in child) || !child.allowedRoles) return true;
            return child.allowedRoles.includes(role);
          })
        };
      }).filter((item) => !item.children || item.children.length > 0);

      if (posSettings?.sell_only_products) {
        items = items
          .filter((item) => item.path !== '/my-services' && item.path !== '/services/jobs')
          .map((item) => {
            if (item.children) {
              return {
                ...item,
                children: item.children.map((branch) => {
                  if (isBranch(branch)) {
                    return {
                      ...branch,
                      children: branch.children.filter((leaf) => leaf.path !== '/reports/sales/services-analysis'),
                    };
                  }
                  return branch;
                }),
              };
            }
            return item;
          });
      }

      if (!posSettings?.enable_delivery) {
        items = items.filter((item) => item.path !== '/deliveries');
      }

      return items;
    },
    [role, permissions, posSettings, whatsappEnabled, shopifyEnabled, theme],
  );

  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const [openBranches, setOpenBranches] = useState<Record<string, boolean>>({});
  const [flyoutPath, setFlyoutPath] = useState<string | null>(null);
  const flyoutCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setOpenMenus((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const item of allMenuItems) {
        if (!item.children?.length) continue;
        if (item.children.some((c) => childIsActive(location.pathname, c)) && !next[item.path]) {
          next[item.path] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    setOpenBranches((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const item of allMenuItems) {
        for (const child of item.children || []) {
          if (!isBranch(child)) continue;
          if (childIsActive(location.pathname, child) && !next[child.pathPrefix]) {
            next[child.pathPrefix] = true;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });

    setFlyoutPath(null);
  }, [location.pathname]);

  useEffect(() => () => {
    if (flyoutCloseTimer.current) clearTimeout(flyoutCloseTimer.current);
  }, []);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      /* offline ok */
    }
    clearLocalUnlock();
    clearAuth();
    if (isDesktopApp()) {
      const setupDone = useDesktopSessionStore.getState().setupComplete;
      navigate(setupDone ? '/unlock' : '/login');
    }
  };

  const handleLock = () => {
    lockScreen();
    navigate('/unlock');
  };

  const handleSyncNow = async () => {
    setSyncBusy(true);
    try {
      const result = await syncNow();
      qc.invalidateQueries({ queryKey: ['customers'] });
      qc.invalidateQueries({ queryKey: ['posItems'] });
      if (result.failed > 0) {
        toast.error(`Synced ${result.done}, ${result.failed} failed`);
      } else if (result.done > 0) {
        toast.success(`Synced ${result.done} item(s)`);
      } else {
        toast.success('Nothing to sync');
      }
    } catch (e) {
      toast.error((e as Error)?.message || 'Sync failed');
    } finally {
      setSyncBusy(false);
    }
  };

  const handleLinkClick = () => {
    setFlyoutPath(null);
    if (typeof window !== 'undefined' && window.innerWidth < 1024 && isSidebarOpen) {
      toggleSidebar();
    }
  };

  const toggleMenu = (path: string, isActive: boolean) => {
    setOpenMenus((prev) => ({ ...prev, [path]: !(prev[path] ?? isActive) }));
  };

  const toggleBranch = (key: string, isActive: boolean) => {
    setOpenBranches((prev) => ({ ...prev, [key]: !(prev[key] ?? isActive) }));
  };

  const clearFlyoutTimer = () => {
    if (flyoutCloseTimer.current) {
      clearTimeout(flyoutCloseTimer.current);
      flyoutCloseTimer.current = null;
    }
  };

  const openFlyout = (path: string) => {
    clearFlyoutTimer();
    setFlyoutPath(path);
  };

  const scheduleFlyoutClose = () => {
    clearFlyoutTimer();
    flyoutCloseTimer.current = setTimeout(() => setFlyoutPath(null), 180);
  };

  const toggleFlyout = (path: string) => {
    setFlyoutPath((prev) => (prev === path ? null : path));
  };

  const flyoutItem = flyoutPath ? menuItems.find((item) => item.path === flyoutPath) : null;

  // ── Legacy collapsed rail (expand button only) ──────────────────────────
  if (!USE_COMPACT_ICON_RAIL && !isSidebarOpen) {
    return (
      <aside className="hidden lg:flex w-16 bg-slate-900 text-white flex-col h-screen flex-shrink-0 items-center py-4 relative z-50">
        <button onClick={toggleSidebar} className="p-2 hover:bg-slate-800 text-gray-400 hover:text-white rounded-lg transition-colors" title="Expand Sidebar">
          <Menu className="w-6 h-6" />
        </button>
      </aside>
    );
  }

  // ── Compact icon rail (collapsed desktop) ─────────────────────────────────
  if (USE_COMPACT_ICON_RAIL && !isSidebarOpen) {
    return (
      <>
        <aside
          className="hidden lg:flex w-14 bg-slate-950 text-white flex-col h-screen flex-shrink-0 relative z-50 border-r border-slate-800/80"
          onMouseLeave={scheduleFlyoutClose}
        >
          <div className="py-3 flex justify-center shrink-0 border-b border-slate-800/80">
            <button
              onClick={toggleSidebar}
              className="p-2 text-slate-400 hover:bg-white/5 hover:text-white rounded-lg transition-colors duration-150"
              title="Expand sidebar"
            >
              {isRtl ? <PanelRightOpen className="w-5 h-5" /> : <PanelLeftOpen className="w-5 h-5" />}
            </button>
          </div>

          <nav className="flex-1 flex flex-col items-center gap-0.5 py-2 overflow-y-auto overscroll-contain w-full px-1.5">
            {menuItems.map((item) => {
              const active = itemIsActive(location.pathname, item);
              const hasChildren = Boolean(item.children?.length);
              const iconClass = `relative flex items-center justify-center w-10 h-10 rounded-lg transition-colors duration-150 ${railIconClass(active)}`;

              if (!hasChildren) {
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    title={labelOf(item)}
                    className={iconClass}
                  >
                    <item.icon className="w-[18px] h-[18px] shrink-0" />
                    {active && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-primary-400 rounded-r-full" />
                    )}
                  </Link>
                );
              }

              return (
                <button
                  key={item.path}
                  type="button"
                  title={labelOf(item)}
                  onClick={() => toggleFlyout(item.path)}
                  onMouseEnter={() => openFlyout(item.path)}
                  className={iconClass}
                >
                  <item.icon className="w-[18px] h-[18px] shrink-0" />
                  {active && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 bg-primary-400 rounded-r-full" />
                  )}
                </button>
              );
            })}
          </nav>

          <div className="py-3 flex flex-col items-center gap-1 shrink-0 border-t border-slate-800/80 overflow-visible">
            {showSync && (
              <button
                onClick={() => void handleSyncNow()}
                disabled={syncBusy || isSyncing}
                title={`Sync Now (${pendingQueue + failedQueue} pending)`}
                className="relative flex items-center justify-center w-10 h-10 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors duration-150 disabled:opacity-50"
              >
                {syncBusy || isSyncing ? (
                  <Loader2 className="w-[18px] h-[18px] animate-spin" />
                ) : (
                  <RefreshCw className="w-[18px] h-[18px]" />
                )}
                {pendingQueue + failedQueue > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-0.5 rounded-full bg-amber-500 text-[9px] font-bold text-white flex items-center justify-center">
                    {pendingQueue + failedQueue > 99 ? '99+' : pendingQueue + failedQueue}
                  </span>
                )}
              </button>
            )}
            {isDesktopApp() && (
              <>
                <button
                  onClick={handleLock}
                  title="Lock screen"
                  className="flex items-center justify-center w-10 h-10 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors duration-150"
                >
                  <Lock className="w-[18px] h-[18px]" />
                </button>
                <button
                  onClick={() => setSwitchOpen(true)}
                  title="Switch cashier"
                  className="flex items-center justify-center w-10 h-10 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors duration-150"
                >
                  <UserRound className="w-[18px] h-[18px]" />
                </button>
              </>
            )}
            {!isCompanyAdmin(role) && !isManager(role) && role !== ROLES.DELIVERY_PERSON && (
              <button
                onClick={() => setMyShiftsOpen(true)}
                title="My Shifts"
                className="flex items-center justify-center w-10 h-10 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors duration-150"
              >
                <Clock className="w-[18px] h-[18px]" />
              </button>
            )}
            <UserAccountMenu compact onLogout={() => void handleLogout()} onMyShifts={role === ROLES.DELIVERY_PERSON ? undefined : () => setMyShiftsOpen(true)} />
          </div>
        </aside>

        <SwitchCashierModal open={switchOpen} onClose={() => setSwitchOpen(false)} />
        {myShiftsOpen && <MyShiftsModal onClose={() => setMyShiftsOpen(false)} />}

        {flyoutItem?.children?.length && (
          <div
            className="hidden lg:block fixed inset-y-0 left-14 z-[60] w-60 bg-slate-900/95 backdrop-blur-sm border-r border-slate-800 shadow-xl"
            onMouseEnter={clearFlyoutTimer}
            onMouseLeave={scheduleFlyoutClose}
          >
            <FlyoutPanel
              item={flyoutItem}
              pathname={location.pathname}
              openBranches={openBranches}
              onToggleBranch={toggleBranch}
              onNavigate={() => setFlyoutPath(null)}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <div
        className="lg:hidden fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm"
        onClick={toggleSidebar}
      />
      <aside className="fixed inset-y-0 start-0 z-50 w-[min(16rem,88vw)] lg:static lg:w-52 bg-slate-900 text-white flex flex-col h-[100dvh] max-h-[100dvh] flex-shrink-0 transition-transform shadow-2xl lg:shadow-none">
        <div className="px-3 sm:px-4 py-4 border-b border-slate-800 flex items-center justify-between gap-2 shrink-0">
          <div className="min-w-0">
            <img
              src={headerLogoSrc}
              alt={headerLogoAlt}
              className={headerLogoClass}
            />
          </div>
          <button onClick={toggleSidebar} className="p-1 hover:bg-slate-800 text-gray-400 hover:text-white rounded transition-colors shrink-0" title="Collapse Sidebar">
            {isRtl ? <PanelRightClose className="w-5 h-5" /> : <PanelLeftClose className="w-5 h-5" />}
          </button>
        </div>
        <nav className="flex-1 min-h-0 p-3 space-y-1 overflow-y-auto overscroll-contain">
          {menuItems.map((item) => {
            const hasChildren = Boolean(item.children?.length);
            const parentActive = isPathActive(location.pathname, item.path);
            const expanded = openMenus[item.path] ?? parentActive;

            if (!hasChildren) {
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={handleLinkClick}
                  className={`flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors ${location.pathname === item.path
                    ? 'bg-primary-600 text-white font-medium'
                    : 'text-gray-400 hover:bg-slate-800 hover:text-white'
                    }`}
                >
                  <item.icon className="w-4 h-4 shrink-0" />
                  <span className="truncate">{labelOf(item)}</span>
                </Link>
              );
            }

            return (
              <div key={item.path} className="space-y-1">
                <button
                  type="button"
                  onClick={() => toggleMenu(item.path, parentActive)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-colors ${parentActive
                    ? 'bg-slate-800 text-white font-medium'
                    : 'text-gray-400 hover:bg-slate-800 hover:text-white'
                    }`}
                >
                  <item.icon className="w-4 h-4 shrink-0" />
                  <span className="flex-1 text-left truncate">{labelOf(item)}</span>
                  <ChevronDown
                    className={`w-4 h-4 shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
                  />
                </button>

                {expanded && (
                  <div className="ml-2 pl-2 border-l border-slate-700 space-y-0.5">
                    {item.children!.map((child) => {
                      if (!isBranch(child)) {
                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={handleLinkClick}
                            className={`block px-3 py-2 text-sm rounded-lg transition-colors ${location.pathname === child.path
                              ? 'bg-primary-600 text-white font-medium'
                              : 'text-gray-400 hover:bg-slate-800 hover:text-white'
                              }`}
                          >
                            {labelOf(child)}
                          </Link>
                        );
                      }

                      const branchKey = child.pathPrefix;
                      const branchActive = childIsActive(location.pathname, child);
                      const branchOpen = openBranches[branchKey] ?? branchActive;

                      return (
                        <div key={branchKey} className="space-y-0.5">
                          <button
                            type="button"
                            onClick={() => toggleBranch(branchKey, branchActive)}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${branchActive || branchOpen
                              ? 'bg-slate-800/80 text-white'
                              : 'text-gray-400 hover:bg-slate-800 hover:text-white'
                              }`}
                          >
                            <span className="flex-1 text-left truncate">{labelOf(child)}</span>
                            <ChevronDown
                              className={`w-3.5 h-3.5 shrink-0 opacity-70 transition-transform ${branchOpen ? 'rotate-180' : ''
                                }`}
                            />
                          </button>

                          {branchOpen && (
                            <div className="ml-2 pl-2 border-l border-slate-700/80 space-y-0.5">
                              {child.children.map((leaf) => (
                                <Link
                                  key={leaf.path}
                                  to={leaf.path}
                                  onClick={handleLinkClick}
                                  className={`block px-3 py-2 text-sm rounded-lg transition-colors ${location.pathname === leaf.path
                                    ? 'bg-primary-600 text-white font-medium'
                                    : 'text-gray-400 hover:bg-slate-800 hover:text-white'
                                    }`}
                                >
                                  {labelOf(leaf)}
                                </Link>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
        <div className="p-3 border-t border-slate-800 shrink-0 space-y-1 pb-[max(0.75rem,env(safe-area-inset-bottom))] relative z-10">
          {showSync && (
            <button
              onClick={() => void handleSyncNow()}
              disabled={syncBusy || isSyncing}
              className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors w-full disabled:opacity-50"
            >
              {syncBusy || isSyncing ? (
                <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4 shrink-0" />
              )}
              Sync Now
              {pendingQueue + failedQueue > 0 ? ` (${pendingQueue + failedQueue})` : ''}
            </button>
          )}
          {isDesktopApp() && (
            <>
              <button onClick={handleLock} className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors w-full">
                <Lock className="w-4 h-4 shrink-0" /> Lock
              </button>
              <button onClick={() => setSwitchOpen(true)} className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors w-full">
                <UserRound className="w-4 h-4 shrink-0" /> Switch cashier
              </button>
            </>
          )}
          {!isCompanyAdmin(role) && !isManager(role) && role !== ROLES.DELIVERY_PERSON && (
            <button onClick={() => setMyShiftsOpen(true)} className="flex items-center gap-3 px-3 py-2.5 text-sm text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors w-full">
              <Clock className="w-4 h-4 shrink-0" /> My Shifts
            </button>
          )}
          <UserAccountMenu onLogout={() => void handleLogout()} menuOpensUp onMyShifts={role === ROLES.DELIVERY_PERSON ? undefined : () => setMyShiftsOpen(true)} />
          <div className="pt-2 mt-1 border-t border-slate-800 flex justify-center">
            <img
              src={platformLogoSrc}
              alt={platformLogoAlt}
              className={platformLogoClass}
            />
          </div>
        </div>
      </aside>
      <SwitchCashierModal open={switchOpen} onClose={() => setSwitchOpen(false)} />
      {myShiftsOpen && <MyShiftsModal onClose={() => setMyShiftsOpen(false)} />}
    </>
  );
}
